import { I18n, TranslateOptions } from 'i18n-js';

import common from './src/locales/common';
import customer from './src/locales/customer';
import mobile from './src/locales/mobile';
import mobilePro from './src/locales/mobile-pro';
import mobileUI from './src/locales/mobile-ui';
import pro from './src/locales/pro';
import web from './src/locales/web';
import webCst from './src/locales/web-cst';
import webProSignup from './src/locales/web-pro-signup';
import type { Locale, Translations } from './src/types';

type ErrorNotifier = (error: Error) => void;

const i18n = new I18n();

const isTestOrDev = process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'development';

const genderBySalutation: Record<Salutation, 'male' | 'female'> = {
  'M.': 'male',
  Mme: 'female',
};

const parseValues = (key: string, values?: Values): string => {
  if (!values) return key;
  if (values.salutation) return `${key}.${genderBySalutation[values.salutation]}`;
  return key;
};

type Salutation = 'M.' | 'Mme';

export type Values = TranslateOptions & { salutation?: Salutation };

type Translate = (prefix: string) => (key: string, values?: Values) => string;

export const translate: Translate = (prefix) => (_key, values) => {
  const key = parseValues(_key, values);
  return i18n.t(key.startsWith('.') ? `${prefix}${key}` : `${key}`, values);
};

const DEFAULT_LOCALE: Locale = 'fr';

const TEST_LOCALE = 'en-BZ';

const SUPPORTED_LOCALES = ['en', 'fr'];

const isLocale = (value: string | null): value is Locale => !!value && SUPPORTED_LOCALES.includes(value);

const getSupportedLocale = (locale: string, defaultLocale: Locale): Locale => {
  // matches the first two or three lowercase letters before a dash or an underscore if any.
  const matches = locale.match(/^[^-_]{2,3}/);
  const match = matches && matches.length ? matches[0] : null;

  return isLocale(match) ? match : defaultLocale;
};

type SetI18nConfigArgs = {
  translations: {
    [locale: string]: object;
  };
  locale?: string;
  defaultLocale?: Locale;
  notifier?: ErrorNotifier;
};

export const setI18nConfig = ({
  translations,
  locale = DEFAULT_LOCALE,
  defaultLocale = DEFAULT_LOCALE,
  notifier,
}: SetI18nConfigArgs) => {
  i18n.store(translations);
  i18n.enableFallback = true;
  i18n.locale = getSupportedLocale(locale, defaultLocale);
  i18n.defaultLocale = locale === TEST_LOCALE ? 'en' : defaultLocale;
  i18n.missingTranslationPrefix = locale === TEST_LOCALE ? '##' : '';
  i18n.missingBehavior = isTestOrDev ? 'error' : 'message';
  i18n.missingTranslation.register('message', (_, scope) => {
    const message = `missing ${scope} translation for ${locale}`;
    if (notifier) notifier(new Error(message));
    return message;
  });
  // message is the translation text as defined in the json file
  i18n.missingPlaceholder = (_, placeholder, text) => {
    const message = `missing ${placeholder} value for ${locale} inside translation "${text}"`;
    if (isTestOrDev) throw new Error(message);
    if (notifier) notifier(new Error(message));
    return message;
  };
};

// For now, this function is only used for testing to change the current locale between tests.
export const setI18nLocale = (locale: Locale = DEFAULT_LOCALE) => {
  i18n.locale = locale;
};

export const getI18nLocale = (): Locale => i18n.locale as Locale;

export {
  common,
  customer,
  mobile,
  mobilePro,
  mobileUI,
  pro,
  web,
  webCst,
  webProSignup,
};

export type {
  Locale,
  Translations,
};

export default i18n;

